import React, { createContext, useContext, useEffect, useRef } from 'react';

import { type MediaCacheWrapper } from './create-cache-driver';
import { getOrCreateCache } from './get-or-create-cache';

export type MediaClientCache = Map<string, MediaCacheWrapper>;

/**
 * Global cache singleton for media clients.
 */
const mediaClientGlobalCache: MediaClientCache = new Map<string, MediaCacheWrapper>();
const MediaClientCacheContext = createContext<MediaClientCache>(mediaClientGlobalCache);

export const MediaClientCacheProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  return (
    <MediaClientCacheContext.Provider value={mediaClientGlobalCache}>
      {children}
    </MediaClientCacheContext.Provider>
  );
};

/**
 * Hook for retrieving a cache for a specific contentId.
 */
export const useMediaClientCache = (): MediaClientCache => {
  const context = useContext(MediaClientCacheContext);

  if (!context) {
    throw new Error('useMediaClientCache must be used within a MediaClientCacheProvider');
  }

  return context;
};

/**
 * Hook for retrieving a cache for a specific contentId.
 */
export const useScopedMediaClientCache = (contentId: string): MediaCacheWrapper => {
  const context = useContext(MediaClientCacheContext);

  if (!context) {
    throw new Error(
      'useScopedMediaClientCache must be used within a MediaClientCacheProvider'
    );
  }

  return getOrCreateCache(contentId, context);
};

/**
 * A hook that invalidates the cache for a specific contentId when the contentId changes.
 */
export const useInvalidateMediaClientCacheForContentId = (contentId: string | null) => {
  const mediaClientCache = useMediaClientCache();
  const previousContentId = useRef<string | null>(null);

  useEffect(() => {
    if (!contentId) {
      return;
    }

    if (contentId !== previousContentId.current) {
      if (mediaClientCache.has(contentId)) {
        mediaClientCache.get(contentId)?.invalidate();
      }

      previousContentId.current = contentId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);
};
